import { createSelector } from '@ngrx/store';

import * as fromDocumentUpload from '../../reducers/document-upload/document-upload.reducer';
import * as fromFeature from '../../reducers/index';
import { selectDocumentUploadMFEToggle } from '../feature/feature.selectors';
import {
  selectIsDivorceFlow,
  selectIsInheritanceFlow,
  selectIsInheritedIraFlow,
  selectIsInternalTransferFlow,
  selectIsMinorFlow,
  selectIsMultiInheritanceFlow,
} from '../triage/triage.selectors';

export const selectDocumentUploadState = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state,
);
export const selectFilesMetadata = createSelector(fromFeature.getDocumentUploadState, (state) => {
  return state.filesMetaData;
});
export const selectClearData = createSelector(fromFeature.getDocumentUploadState, (state) => {
  return state.clearData;
});
export const selectIsDocumentInState = createSelector(
  selectDocumentUploadState,
  fromDocumentUpload.getHasClientUploadedFile,
);
export const selectDocumentUploadAccountTypes = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => {
    return state.accountTypes;
  },
);

export const selectWldStandardDocuments = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.wldStandardDocuments ?? [],
);

export const selectWldLetterDocuments = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.wldLetterDocuments ?? [],
);

export const selectWldTaxDocuments = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.wldTaxDocuments ?? [],
);

export const selectWldDocuments = createSelector(
  selectWldStandardDocuments,
  selectWldLetterDocuments,
  selectWldTaxDocuments,
  (standard, letter, tax) => [...standard, ...letter, ...tax],
);

export const selectIsWldDocUploadEnabled = createSelector(
  selectDocumentUploadMFEToggle,
  selectIsInheritanceFlow,
  selectIsMultiInheritanceFlow,
  selectIsInheritedIraFlow,
  selectIsInternalTransferFlow,
  selectIsMinorFlow,
  selectIsDivorceFlow,
  (
    toggleOn,
    isInheritance,
    isMultiInheritance,
    isInheritedIra,
    isInternalTransfer,
    isMinor,
    isDivorce,
  ) =>
    !!toggleOn &&
    (isInheritance ||
      isMultiInheritance ||
      isInheritedIra ||
      isInternalTransfer ||
      isMinor ||
      isDivorce),
);

export const selectHasAcceptedWldTerms = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.hasAcceptedWldTerms,
);

export const selectWldTermsContent = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.termsContent,
);

export const selectWldTermsLoading = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.termsLoading,
);

export const selectWldTermsAccepting = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => !!state.termsAccepting,
);

export const selectWldTermsAcceptError = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.termsAcceptError ?? null,
);

export const selectWldConsentStatus = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.consentStatus ?? null,
);

export const selectWldConsentStatusError = createSelector(
  fromFeature.getDocumentUploadState,
  (state) => state.consentStatusError ?? null,
);

export const selectWldConsentChecked = createSelector(
  selectWldConsentStatus,
  selectWldConsentStatusError,
  (status, error) => !!status || !!error,
);

export const selectShouldShowWldTermsModal = createSelector(
  selectHasAcceptedWldTerms,
  selectWldConsentChecked,
  selectIsWldDocUploadEnabled,
  (hasAccepted, consentChecked, isWldDocUploadEnabled) =>
    consentChecked && !hasAccepted && isWldDocUploadEnabled,
);

// Inheritance, multi-inheritance, and inherited-ira share COO-INHERITANCE
// (they all use the same downstream doc-management journey config).
export const selectDocUploadJourneyCode = createSelector(
  selectIsInternalTransferFlow,
  selectIsDivorceFlow,
  selectIsMinorFlow,
  (isInternalTransfer, isDivorce, isMinor) => {
    if (isInternalTransfer) return 'COO-INTERNAL-TRANSFER';
    if (isDivorce) return 'COO-DIVORCE';
    if (isMinor) return 'COO-MINOR';
    return 'COO-INHERITANCE';
  },
);
